// How a project's work is LABELLED for display. Run A, 2026-08-18.
//
// A project used to have exactly one service string, so every card just printed
// projects.service_type. It can now have several service lines, each with its
// own sub and dates, so the label is derived: the ordered line names joined, or
// the legacy audit string when a row predates the backfill and has no lines.
//
// The display names come embedded from service_catalog, so there is no frontend
// copy of the vocabulary to drift.
import type { ProjectServiceLine } from '../../../shared/types';

interface Labelable {
  service_type_raw?: string | null;
  category_raw?: string | null;
  project_services?: ProjectServiceLine[] | null;
}

export function projectServiceLines(row: Labelable): ProjectServiceLine[] {
  return [...(row.project_services ?? [])].sort(
    (a, b) => (a.line_order ?? 0) - (b.line_order ?? 0),
  );
}

/** The lines that are actual work. Chargebacks are money, not services. */
export function projectWorkLines(row: Labelable): ProjectServiceLine[] {
  return projectServiceLines(row).filter((l) => l.line_role !== 'chargeback');
}

/** The lines that take money back off a sub. */
export function projectChargebackLines(row: Labelable): ProjectServiceLine[] {
  return projectServiceLines(row).filter((l) => l.line_role === 'chargeback');
}

/** One line: its catalog display name, falling back to the code, then the scope words. */
export function serviceLineLabel(line: ProjectServiceLine): string {
  if (line.line_role === 'chargeback') return 'Charge back to sub';
  return line.service_catalog?.display_name ?? line.service_code ?? 'Unnamed service';
}

/**
 * The project's work, in words. Several lines join with " + ". Returns null when
 * there is nothing to show at all, so callers can keep their own empty state.
 */
export function projectServiceLabel(row: Labelable): string | null {
  const lines = projectWorkLines(row);
  if (lines.length > 0) {
    return lines
      .map((l) => {
        const base = serviceLineLabel(l);
        // The escape hatch has no meaningful name of its own — show the words
        // the AM actually wrote instead of the word "Other".
        if (l.service_code === 'OTHER' && l.scope_text) return l.scope_text;
        return base;
      })
      .join(' + ');
  }
  return row.service_type_raw ?? null;
}

/** The division label for a project, for the slot the old free-text Category filled. */
export function projectDivisionLabel(row: Labelable): string | null {
  const lines = projectWorkLines(row);
  const div = lines.find((l) => l.service_catalog?.division_id)?.service_catalog?.division_id;
  return div ?? row.category_raw ?? null;
}
